import React, { useState, useEffect, useRef } from "react";
import { parseISO, format } from "date-fns";
import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Switch from "@material-ui/core/Switch";
import Paper from "@material-ui/core/Paper";


import Grid from '@mui/material/Grid';
import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { TagsContainer } from "../TagsContainer";
import { validateDocument, OUTRO_MAX_LENGTH, LABEL_MAX_LENGTH } from "../../utils/documentValidation";
// import AsyncSelect from "../AsyncSelect";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},


}));

const formatDateForInput = (date) => {
	if (!date) return '';

	// Se já está no formato YYYY-MM-DD, retorna como está
	if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
		return date;
	}

	// Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
	const d = new Date(date);
	if (isNaN(d.getTime())) return '';

	// Usar métodos locais para evitar problemas de timezone
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
	if (!dateString) return null;

	// Se já está no formato YYYY-MM-DD, retorna como está
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
		return dateString;
	}

	// Se é uma data ISO, extrai apenas a parte da data
	if (dateString.includes('T')) {
		return dateString.split('T')[0];
	}

	return dateString;
};


const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(250, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
	documents: Yup.array().of(
		Yup.object().shape({
			type: Yup.string().oneOf(["CPF", "CNPJ", "OUTRO"]).required(i18n.t("documents.typeRequired")),
			// #4098 — rótulo obrigatório no documento livre: sem ele, dois "Outro"
			// no mesmo contato ficam indistinguíveis.
			label: Yup.string()
				.max(LABEL_MAX_LENGTH, i18n.t("documents.labelMaxLength", { max: LABEL_MAX_LENGTH }))
				.when("type", {
					is: "OUTRO",
					then: (schema) => schema.trim().required(i18n.t("documents.labelRequired")),
					otherwise: (schema) => schema.nullable()
				}),
			number: Yup.string()
				.required(i18n.t("documents.numberRequired"))
				.test('document-validation', function(value) {
					const { type } = this.parent;

					if (!value || !type) return true; // Deixa outros validators lidarem com obrigatório

					if (type === 'CPF') {
						const cleanNumber = value.replace(/\D/g, '');
						if (cleanNumber.length !== 11) {
							return this.createError({ message: i18n.t("documents.cpfLength") });
						}
						if (!validateDocument({ type, number: cleanNumber })) {
							return this.createError({ message: i18n.t("documents.cpfInvalid") });
						}
						return true;
					}

					if (type === 'CNPJ') {
						// CNPJ aceita alfanumérico (RFB 2026+).
						const cleanCnpj = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
						if (cleanCnpj.length !== 14) {
							return this.createError({ message: i18n.t("documents.cnpjLength") });
						}
						if (!validateDocument({ type, number: cleanCnpj })) {
							return this.createError({ message: i18n.t("documents.cnpjInvalid") });
						}
						return true;
					}

					if (type === 'OUTRO') {
						// Documento livre (#4233): sem DV, só formato (A-Z/0-9, até 15).
						const cleanOutro = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
						if (!cleanOutro.length) {
							return this.createError({ message: i18n.t("documents.outroRequired") });
						}
						if (cleanOutro.length > OUTRO_MAX_LENGTH) {
							return this.createError({
								message: i18n.t("documents.outroMaxLength", { max: OUTRO_MAX_LENGTH })
							});
						}
						return true;
					}

					return true;
				}),
			isActive: Yup.boolean()
		})
	),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);

	const initialState = {
		name: "",
		number: "",
		email: "",
		documents: [],
		disableBot: false,
		lgpdAcceptedAt: "",
		birthDate: "",
		notes: ""
	};

	const [contact, setContact] = useState(initialState);
	const [numberPreview, setNumberPreview] = useState(null);
	const [verifyingNumber, setVerifyingNumber] = useState(false);
	const [confirmInvalid, setConfirmInvalid] = useState(null); // { values, actions } quando aguarda confirmação
	const [disableBot, setDisableBot] = useState(false);
	// #4292 — fixa a observacao dentro da conversa deste contato
	const [notesPinned, setNotesPinned] = useState(false);
	const [notesExpanded, setNotesExpanded] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedQueue, setSelectedQueue] = useState(null);
	const [queues, setQueues] = useState([]);
	const [allQueues, setAllQueues] = useState([]);
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedTags, setSelectedTags] = useState([]);


	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Pré-visualização do número normalizado (DDI + nono dígito). Mostra ao usuário
	// como o número será salvo — útil para flagrar DDD errado/omitido antes de salvar.
	const previewTimer = useRef(null);
	const fetchNumberPreview = (rawNumber) => {
		if (previewTimer.current) clearTimeout(previewTimer.current);
		// Preserva o "+" inicial (número internacional). Só dígitos no resto.
		const trimmed = (rawNumber || "").trim();
		const hasPlus = trimmed.startsWith("+");
		const digits = trimmed.replace(/\D/g, "");
		const query = hasPlus ? `+${digits}` : digits;
		if (digits.length < 8) {
			setNumberPreview(null);
			setVerifyingNumber(false);
			return;
		}
		previewTimer.current = setTimeout(async () => {
			if (isMounted.current) setVerifyingNumber(true);
			try {
				const { data } = await api.get("/contacts/preview-number", {
					params: { number: query, verify: true }
				});
				if (isMounted.current) setNumberPreview(data);
			} catch {
				if (isMounted.current) setNumberPreview(null);
			} finally {
				if (isMounted.current) setVerifyingNumber(false);
			}
		}, 500);
	};

	useEffect(() => {
		if (!open || searchParam.length < 3) {
			setLoading(false);
			setSelectedQueue("");
			return;
		}
		const delayDebounceFn = setTimeout(() => {
			setLoading(true);
			const fetchUsers = async () => {
				try {
					const { data } = await api.get("/users/", {
						params: { searchParam },
					});
					setOptions(data.users);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchUsers();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, open]);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return {
						...prevState,
						...initialValues,
						// Formatar a data corretamente
						birthDate: formatDateForInput(initialValues.birthDate),
						// null viraria input nao-controlado no Formik
						notes: initialValues.notes || "",
						documents: initialValues.documents || []
					};
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					setContact({
						...data,
						birthDate: formatDateForInput(data.birthDate),
						notes: data.notes || "",
						documents: data.documents || []
					});
					setDisableBot(data.disableBot)
						setNotesPinned(!!data.notesPinned)
						setNotesExpanded(!!data.notesExpanded)

					// Preenche automaticamente os campos de Wallet e Queue
					if (data.contactWallets && data.contactWallets.length > 0) {
						const wallet = data.contactWallets[0].wallet;
						const queue = data.contactWallets[0].queue;

						setSelectedUser({
							id: wallet.id,
							name: wallet.name,
						});
						setSelectedQueue(queue.id);

						// Atualiza as filas disponíveis no campo de seleção
						setQueues([{ id: queue.id, name: queue.name }]);
					} else {
						// Limpa os valores quando não há contactWallets
						setSelectedUser(null);
						setSelectedQueue(null);
						setQueues([]);
					}
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setSelectedUser(null);
		setSelectedQueue(null);
		setQueues([]);
		setSelectedTags([]);
	};

	const showDocumentWarnings = (warnings) => {
		if (!warnings || warnings.length === 0) return;
		warnings.forEach(w => {
			const names = (w.usedBy || []).map(c => c.name).join(", ");
			toast.info(
				i18n.t("documents.duplicateWarning", {
					type: w.type,
					number: w.number,
					contacts: names
				})
			);
		});
	};

	const handleSaveContact = async values => {
		try {
			// Preparar os dados com a data corretamente formatada
			const contactData = {
				...values,
				disableBot: disableBot,
				notesPinned: notesPinned,
				notesExpanded: notesExpanded,
				birthDate: parseDateFromInput(values.birthDate)
			};

			if (contactId) {
				if (!selectedUser && !selectedQueue) {
					delete contact.contactWallets;
					await api.delete(`/contacts/wallet/${contactId}`);
				}

				const { contactWallets, ...valuesWithoutWallets } = contactData;
				delete contact.contactWallets;

				const { data: updated } = await api.put(`/contacts/${contactId}`, contactData);
				showDocumentWarnings(updated?.documentWarnings);

				if (selectedUser && selectedQueue && selectedUser !== null && selectedQueue !== null) {
					await api.put(`/contacts/wallet/${contactId}`, {
						wallets: {
							userId: selectedUser.id,
							queueId: selectedQueue,
						},
					});
				}

				handleClose();

			} else {
				delete contactData.contactWallets;

				const { data } = await api.post("/contacts", contactData);
				showDocumentWarnings(data?.documentWarnings);

				if (data.id && selectedUser && selectedQueue) {
					await api.put(`/contacts/wallet/${data.id}`, {
						wallets: {
							userId: selectedUser.id,
							queueId: selectedQueue,
						},
					});
				}

				// Sincronizar as tags após criar o contato
				if (data.id && selectedTags.length > 0) {
					await api.post("/tags/sync", {
						contactId: data.id,
						tags: selectedTags,
					});
				}

				if (onSave) {
					onSave(data);
				}

				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						// Número inválido (ex: DDD omitido) → confirma antes de salvar.
						// Não bloqueia de vez: casos legítimos (número antigo sem nono
						// dígito) passam quando o usuário confirma.
						// Se o canal CONFIRMOU que o numero existe, e valido de fato
						// (ainda que o palpite local marque invalido) - salva direto.
						const channelConfirmed = numberPreview?.checked?.exists === true;
						if (
							!values.isGroup &&
							!channelConfirmed &&
							numberPreview &&
							!numberPreview.valid
						) {
							actions.setSubmitting(false);
							setConfirmInvalid({ values });
							return;
						}
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue, validateField, setFieldTouched }) => (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<Field
									as={TextField}
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<Field
											as={TextField}
											label={i18n.t("contactModal.form.number")}
											name="number"
											error={
												(touched.number && Boolean(errors.number)) ||
												// Só marca erro quando a verificação no canal NEGA o número.
												// O palpite local (valid) não bloqueia — é só dica.
												(numberPreview?.checked
													? numberPreview.checked.exists === false
													: false)
											}
											helperText={
												(touched.number && errors.number) ||
												// 0) Enquanto verifica no canal, mostra o loading.
												(verifyingNumber
													? "Verificando no WhatsApp…"
													: // 1) Verificação real no WhatsApp tem prioridade.
													numberPreview?.checked
													? numberPreview.checked.exists
														? numberPreview.checked.number &&
														  numberPreview.checked.number !== (numberPreview.canonical || "")
															? `✓ Tem WhatsApp. Número real: ${numberPreview.checked.number} (será salvo assim)`
															: "✓ Número tem WhatsApp"
														: "⚠ Este número não tem WhatsApp — você pode salvar mesmo assim"
													// 2) Sem verificação: dica de formatação local.
													: numberPreview
														? numberPreview.valid
															? `Será salvo como: ${numberPreview.formatted}`
															: `Será salvo como: ${numberPreview.formatted} — confira DDD e nono dígito`
														: "Brasil: DDD + número. Outro país: comece com + e o DDI (ex: +1 202 555 0123)")
											}
											placeholder="5567998576395 ou +12025550123"
											variant="outlined"
											margin="dense"
											fullWidth
											InputProps={{
												endAdornment: verifyingNumber ? (
													<CircularProgress color="inherit" size={18} />
												) : null
											}}
											onChange={(e) => {
												setFieldValue("number", e.target.value);
												fetchNumberPreview(e.target.value);
											}}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<TextField
											label="LID (Linked Device ID)"
											value={contact.lid || ""}
											disabled
											fullWidth
											margin="dense"
											variant="outlined"
											helperText="Identificador do dispositivo vinculado"
										/>
									</Grid>
								</Grid>
								<div>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								{/* Seção de Documentos */}
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									<b>Documentos</b>
								</Typography>

								<FieldArray name="documents">
									{({ push, remove }) => (
										<>
											{values.documents &&
												values.documents.length > 0 &&
												values.documents.map((doc, index) => (
													<Grid
														container
														spacing={2}
														key={`document-${index}`}
														style={{ marginBottom: 8, alignItems: "center" }}
													>
														<Grid item xs={3}>
															<FormControl variant="outlined" fullWidth margin="dense">
																<InputLabel>{i18n.t("documents.type")}</InputLabel>
																<Field
																	as={Select}
																	name={`documents[${index}].type`}
																	label={i18n.t("documents.type")}
																>
																	<MenuItem value="CPF">{i18n.t("documents.cpf")}</MenuItem>
																	<MenuItem value="CNPJ">{i18n.t("documents.cnpj")}</MenuItem>
																	<MenuItem value="OUTRO">{i18n.t("documents.outro")}</MenuItem>
																</Field>
															</FormControl>
														</Grid>
														{values.documents[index]?.type === 'OUTRO' && (
															<Grid item xs={4}>
																<TextField
																	label={i18n.t("documents.label")}
																	value={values.documents[index]?.label || ''}
																	variant="outlined"
																	margin="dense"
																	placeholder={i18n.t("documents.labelPlaceholder")}
																	fullWidth
																	inputProps={{ maxLength: LABEL_MAX_LENGTH }}
																	error={Boolean(touched.documents?.[index]?.label && errors.documents?.[index]?.label)}
																	helperText={touched.documents?.[index]?.label && errors.documents?.[index]?.label}
																	onChange={(e) => setFieldValue(`documents[${index}].label`, e.target.value)}
																	onBlur={() => {
																		setFieldTouched(`documents[${index}].label`, true);
																		validateField(`documents[${index}].label`);
																	}}
																/>
															</Grid>
														)}
														<Grid item xs={values.documents[index]?.type === 'OUTRO' ? 4 : 8}>
															<TextField
																label={i18n.t("documents.number")}
																value={values.documents[index]?.number || ''}
																variant="outlined"
																margin="dense"
																placeholder={i18n.t("documents.placeholder")}
																fullWidth
																error={
																	touched.documents &&
																	touched.documents[index] &&
																	touched.documents[index].number &&
																	errors.documents &&
																	errors.documents[index] &&
																	errors.documents[index].number
																}
																helperText={
																	touched.documents &&
																	touched.documents[index] &&
																	touched.documents[index].number &&
																	errors.documents &&
																	errors.documents[index] &&
																	errors.documents[index].number
																}
																onChange={(e) => {
																	// Aceita alfanumérico (CNPJ alfanumérico — RFB 2026+).
																	// Mantém A-Z/0-9, normaliza letras para maiúsculo.
																	const currentType = values.documents[index]?.type;
																	const isOutro = currentType === 'OUTRO';
																	let cleanValue = e.target.value
																		.replace(/[^A-Za-z0-9]/g, '')
																		.toUpperCase();

																	// "Outro" é campo livre com teto de 15 (#4233).
																	if (isOutro) {
																		cleanValue = cleanValue.slice(0, OUTRO_MAX_LENGTH);
																	}
																	setFieldValue(`documents[${index}].number`, cleanValue);

																	// Auto-detecta: CPF só se 11 dígitos; CNPJ se 14 alfanuméricos.
																	// Não roda quando o usuário escolheu "Outro" — senão um valor
																	// de 11/14 caracteres viraria CPF/CNPJ sozinho e o tipo
																	// escolhido nunca se sustentaria (#4233).
																	if (isOutro) {
																		setFieldTouched(`documents[${index}].number`, true);
																		validateField(`documents[${index}].number`);
																		return;
																	}

																	const isAllDigits = /^\d+$/.test(cleanValue);
																	if (cleanValue.length === 11 && isAllDigits) {
																		setFieldValue(`documents[${index}].type`, 'CPF');
																		setFieldTouched(`documents[${index}].number`, true);
																		validateField(`documents[${index}].number`);
																	} else if (cleanValue.length === 14) {
																		setFieldValue(`documents[${index}].type`, 'CNPJ');
																		setFieldTouched(`documents[${index}].number`, true);
																		validateField(`documents[${index}].number`);
																	}
																}}
																onBlur={() => {
																	setFieldTouched(`documents[${index}].number`, true);
																	validateField(`documents[${index}].number`);
																}}
															/>
														</Grid>
														<Grid item xs={1}>
															<IconButton
																size="small"
																onClick={() => remove(index)}
																color="secondary"
															>
																<DeleteOutlineIcon />
															</IconButton>
														</Grid>
													</Grid>
												))}
											<div style={{ marginTop: 8 }}>
												<Button
													variant="outlined"
													color="primary"
													onClick={() => push({
														type: "CPF",
														number: "",
														label: "",
														isActive: true
													})}
												>
													{i18n.t("documents.addDocument")}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
								<div>
									<Field
										as={TextField}
										label="Data de Nascimento"
										name="birthDate"
										type="date"
										InputLabelProps={{
											shrink: true,
										}}
										fullWidth
										margin="dense"
										variant="outlined"
										helperText="Data de nascimento para mensagens automáticas de aniversário"
										onChange={(e) => {
											const formattedDate = parseDateFromInput(e.target.value);
											setFieldValue('birthDate', formattedDate);
										}}
									/>
								</div>

								{/* #4292 — observacao do CONTATO: vale para todos os
									 atendimentos dele, diferente da observacao do ticket.
									 Campo e chaves ficam na MESMA caixa: as chaves so fazem
									 sentido lendo o texto que elas controlam. */}
								<div>
									<Paper
										variant="outlined"
										style={{ padding: 12, marginTop: 8 }}
									>
										<Typography
											variant="subtitle1"
											style={{ marginBottom: 4 }}
										>
											<b>Observações</b>
										</Typography>
										<Field
											as={TextField}
											name="notes"
											placeholder="Ex.: cliente prefere contato pela manhã; não enviar áudio"
											multiline
											minRows={4}
											fullWidth
											margin="dense"
											variant="outlined"
											helperText="Anotações sobre este contato, visíveis em todos os atendimentos"
										/>
										<Typography variant="body2" style={{ marginTop: 4 }}>
											<Switch
												size="small"
												checked={notesPinned}
												onChange={() => setNotesPinned(!notesPinned)}
												name="notesPinned"
											/>
											Exibir na conversa
										</Typography>
										{/* Escolher COMO abre so faz sentido se ela aparece. */}
										{notesPinned && (
											<Typography variant="body2">
												<Switch
													size="small"
													checked={notesExpanded}
													onChange={() => setNotesExpanded(!notesExpanded)}
													name="notesExpanded"
												/>
												Já abrir expandida
											</Typography>
										)}
									</Paper>
								</div>

								<div>
									<TagsContainer
										contact={contact}
										className={classes.textField}
										onTagsChange={setSelectedTags}
									/>
								</div>

								<Grid item>
									<Typography
										variant="subtitle1"
										gutterBottom
										style={{ marginTop: 24, marginBottom: 8 }}
									>
										<b>{i18n.t("contactModal.form.assignWallet")}</b>
									</Typography>
								</Grid>
								{/* <div className={classes.blockCustomerField}> */}
								<Grid container spacing={2}>
									<Grid xs={12} sm={6} xl={6} item>
										<Autocomplete
											value={selectedUser}
											fullWidth
											getOptionLabel={(option) => `${option.name}`}
											onChange={(e, newValue) => {
												setSelectedUser(newValue);
												if (
													newValue != null &&
													Array.isArray(newValue.queues)
												) {
													if (newValue.queues.length === 1) {
														setSelectedQueue(newValue.queues[0].id);
													}
													setQueues(newValue.queues);
												} else {
													setQueues(allQueues);
													setSelectedQueue(null);
													setSelectedUser(null);
												}
											}}
											options={options}
											filterOptions={(options, { inputValue }) =>
												options.filter((option) =>
													option.name
														.toLowerCase()
														.includes(inputValue.toLowerCase())
												)
											}
											freeSolo
											autoHighlight
											noOptionsText={i18n.t("transferTicketModal.noOptions")}
											loading={loading}
											renderOption={(props, option) => (
												<li {...props}>
													<span style={{ marginLeft: 8 }}>{option.name}</span>
												</li>
											)}
											renderInput={(params) => (
												<TextField
													{...params}
													label={i18n.t("transferTicketModal.fieldLabel")}
													variant="outlined"
													onChange={(e) => setSearchParam(e.target.value)}
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<React.Fragment>
																{loading ? (
																	<CircularProgress color="inherit" size={20} />
																) : null}
																{params.InputProps.endAdornment}
															</React.Fragment>
														),
													}}
												/>
											)}
										/>
									</Grid>

									<Grid xs={12} sm={6} xl={6} item>
										<FormControl variant="outlined" fullWidth>
											<InputLabel>
												{i18n.t("transferTicketModal.fieldQueueLabel")}
											</InputLabel>
											<Select
												value={selectedQueue}
												onChange={(e) => setSelectedQueue(e.target.value)}
												label={i18n.t(
													"transferTicketModal.fieldQueuePlaceholder"
												)}
											>
												{queues.map((queue) => (
													<MenuItem key={queue.id} value={queue.id}>
														{queue.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
								</Grid>

								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									<Switch
										size="small"
										checked={disableBot}
										onChange={() =>
											setDisableBot(!disableBot)
										}
										name="disableBot"
									/>
									{i18n.t("contactModal.form.chatBotContact")}
								</Typography>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.whatsapp")} {contact?.whatsapp ? contact?.whatsapp.name : ""}
								</Typography>
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.termsLGDP")} {contact?.lgpdAcceptedAt ? format(new Date(contact?.lgpdAcceptedAt), "dd/MM/yyyy 'às' HH:mm") : ""}
								</Typography>

								{/* <Typography variant="subtitle1" gutterBottom>{i18n.t("contactModal.form.customer_portfolio")}</Typography> */}
								{/* <div style={{ marginTop: 10 }}>
									<AsyncSelect url="/users" dictKey={"users"}
										initialValue={values.user} width="100%" label={i18n.t("contactModal.form.attendant")}
										onChange={(event, value) => setFieldValue("userId", value ? value.id : null)} />
								</div>
								<div style={{ marginTop: 10 }}>
									<AsyncSelect url="/queue" dictKey={null}
										initialValue={values.queue} width="100%" label={i18n.t("contactModal.form.queue")}
										onChange={(event, value) => setFieldValue("queueId", value ? value.id : null)} />
								</div> */}
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>

			{/* Confirmação para número que parece inválido (ex: DDD omitido). */}
			<Dialog open={Boolean(confirmInvalid)} onClose={() => setConfirmInvalid(null)}>
				<DialogTitle>Número parece inválido</DialogTitle>
				<DialogContent dividers>
					<Typography>
						O número será salvo como{" "}
						<strong>{numberPreview?.formatted}</strong> e parece estar incompleto
						ou com DDD incorreto. Deseja salvar mesmo assim?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmInvalid(null)} color="secondary">
						Revisar número
					</Button>
					<Button
						onClick={() => {
							const values = confirmInvalid?.values;
							setConfirmInvalid(null);
							if (values) handleSaveContact(values);
						}}
						color="primary"
						variant="contained"
					>
						Salvar mesmo assim
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default ContactModal;
